import { Formik } from 'formik';
import React from 'react';
import { useAlert } from '../../../../AlertProvider';
import * as Yup from 'yup';

import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useQueryClient } from '@tanstack/react-query';
import { Appointment, cancelSession } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { UserRole } from '@uplift/types';

import { Button } from '../../../../Button';
import {
  CancelSessionReasonAutocomplete
} from '../../../Modals/cancel-session-modal/cancel-session-reason-autocomplete.component';

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(0.5),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(3),
  }
}));


export type CancelSessionBlockProps = {
  shown?: boolean;
  appointment: Appointment;
  onClose: () => void;
  mode: 'all' | 'single';
};


export const CancelSessionBlock: React.FC<CancelSessionBlockProps> = (props) => {
  const { classes } = useStyles();
  const me = useCurrentuser();
  const l10n = useL10n();
  const queryClient = useQueryClient();
  const alert = useAlert();

  return (
    <Formik
      enableReinitialize
      initialValues={{
        key: props.shown,
        appointmentId: props.appointment.id,
        parentId: props.appointment.appointmentSeriesId,
        fromDatetime: props.appointment.fromDatetime,
        cancelReason: '',
        recurring: props.mode,
      }}
      validationSchema={Yup.object().shape({
        cancelReason: Yup.string().nullable().required(l10n.validations.required),
      })}
      validateOnMount
      onSubmit={async (values) => {
        await cancelSession({
          sessionId: values.appointmentId,
          parentId: values.parentId,
          cancelReasonId: values.cancelReason,
          recurring: values.recurring,
          chargeLate: false,
          fromDatetime: values.fromDatetime,
        });

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [ 'next-sessions' ] }),
          queryClient.invalidateQueries({ queryKey: [ 'calendar' ] }),
          queryClient.invalidateQueries({ queryKey: [ 'recurringList', props.appointment.appointmentSeriesId ] }),
        ]);

        alert.success({
          all: l10n.sessions.successfullyCanceledAll,
          single: l10n.sessions.successfullyCanceled,
        }[values.recurring]);
      }}
    >
      {formik => (
        <Box className={classes.root}>
          <CancelSessionReasonAutocomplete
            label={l10n.schedule.cancelAppointment.whyAreYouCanceling}
            name="cancelReason"
            userRole={me.role as keyof typeof UserRole}
            fromDatetime={props.appointment.fromDatetime}
            excludeReasons={[
              'technicalDifficulties',
              'clientProfileIncomplete',
              'clientDidNotShow',
              'clientHasInsuranceIssues',
            ]}
          />

          <Box className={classes.actions}>
            <Button variant="outlined" size="small" onClick={props.onClose}>
              {props.mode === 'all'
                ? l10n.schedule.cancelAppointment.keepAllSessions
                : l10n.schedule.cancelAppointment.keepSession}
            </Button>
            <Button
              variant="contained" size="small" color="error"
              disabled={!formik.isValid} loading={formik.isSubmitting}
              onClick={formik.handleSubmit}
            >
              {props.mode === 'all'
                ? l10n.schedule.cancelAppointment.cancelAllSessions
                : l10n.schedule.cancelAppointment.cancelSession}
            </Button>
          </Box>
        </Box>
      )}
    </Formik>
  );
};
